import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { subDays } from "date-fns";
import { AlertTriangle, BadgeDollarSign, Contact2, RefreshCw, Target, UserRoundCheck } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { PageHeader } from "../../components/domain/Bits";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";

interface DashboardData {
  scope: "self" | "team" | "tenant";
  filters: { from_date: string; to_date: string; territory: string | null; territories: string[] };
  summary: {
    referring_contacts: number; referrals: number; conversions: number;
    conversion_rate: number; follow_ups_due: number; follow_ups_overdue: number;
    visits_pending_verification: number; visits_rejected: number; visits_verified: number;
  };
  commission: { paid: number; unpaid: number; non_commissionable: number; currency: string };
  pipeline: Array<{ stage: string; count: number }>;
  staff_performance: Array<{ user_id: string; name: string; contacts: number; referrals: number; conversions: number; conversion_rate: number }>;
  territory_performance: Array<{ territory: string; referrals: number; conversions: number }>;
}

const day = (value: Date) => value.toISOString().slice(0, 10);

function MetricCard({ label, value, detail, icon: Icon, to }: {
  label: string; value: string | number; detail?: string; icon: React.ElementType; to?: string;
}) {
  const body = (
    <div className="rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-300">
      <div className="flex items-center justify-between text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}<Icon className="h-4 w-4" />
      </div>
      <div className="mt-2 text-2xl font-semibold tracking-tight text-slate-900">{value}</div>
      {detail && <div className="mt-1 text-xs text-slate-500">{detail}</div>}
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}

export default function Dashboard() {
  const [fromDate, setFromDate] = useState(day(subDays(new Date(), 29)));
  const [toDate, setToDate] = useState(day(new Date()));
  const [territory, setTerritory] = useState("");
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get<DashboardData>("/tenant/dashboard", {
        params: { from_date: fromDate, to_date: toDate, territory: territory || undefined },
      });
      setData(response.data);
    } catch {
      setError("Could not load the dashboard. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, territory]);

  useEffect(() => { load(); }, [load]);

  const currency = data?.commission.currency || "CAD";
  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={data?.scope === "self" ? "Your referral and sales performance" : "Referral, conversion, pipeline, and commission overview"}
      />
      <main className="mx-auto max-w-7xl space-y-5 p-6 md:p-8" data-testid="role-dashboard">
        <section className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 md:grid-cols-[1fr_1fr_1fr_auto]">
          <label className="text-xs font-medium text-slate-600">From
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="mt-1" />
          </label>
          <label className="text-xs font-medium text-slate-600">To
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} className="mt-1" />
          </label>
          <label className="text-xs font-medium text-slate-600">Territory
            <select
              value={territory}
              onChange={(e) => setTerritory(e.target.value)}
              className="mt-1 h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
              data-testid="dashboard-territory-filter"
            >
              <option value="">All territories</option>
              {(data?.filters.territories || []).map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
          <Button variant="outline" onClick={load} disabled={loading} className="self-end">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </section>

        {error && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>}
        {loading && !data ? <div className="py-16 text-center text-sm text-slate-500">Loading dashboard…</div> : data && (
          <>
            <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-9">
              <MetricCard label="Referring contacts" value={data.summary.referring_contacts} icon={Contact2} to="/contacts" />
              <MetricCard label="New referrals" value={data.summary.referrals} icon={UserRoundCheck} to="/contacts" />
              <MetricCard label="Conversions" value={data.summary.conversions} icon={Target} to="/my-performance" />
              <MetricCard label="Conversion rate" value={`${data.summary.conversion_rate}%`} icon={Target} />
              <MetricCard label="Follow-ups due" value={data.summary.follow_ups_due} icon={RefreshCw} to="/" />
              <MetricCard label="Overdue" value={data.summary.follow_ups_overdue} icon={AlertTriangle} to="/" />
              <MetricCard label="Visits pending" value={data.summary.visits_pending_verification} icon={RefreshCw} to="/calendar" />
              <MetricCard label="Visits rejected" value={data.summary.visits_rejected} icon={AlertTriangle} to="/calendar" />
              <MetricCard label="Visits verified" value={data.summary.visits_verified} icon={UserRoundCheck} to="/calendar" />
            </section>

            <section className="grid gap-4 lg:grid-cols-3">
              <div className="rounded-lg border border-slate-200 bg-white p-4 lg:col-span-2">
                <h2 className="text-sm font-semibold">Pipeline status</h2>
                <p className="mb-4 text-xs text-slate-500">Current opportunities by stage</p>
                {data.pipeline.length ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={data.pipeline} margin={{ left: -15 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="stage" tick={{ fontSize: 11 }} interval={0} angle={-12} height={55} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#0f172a" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <p className="py-20 text-center text-sm text-slate-500">No opportunities in this view.</p>}
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <h2 className="flex items-center gap-2 text-sm font-semibold"><BadgeDollarSign className="h-4 w-4" /> Commission status</h2>
                <p className="mb-5 text-xs text-slate-500">Snapshot amounts from conversions in this period</p>
                {[
                  ["Unpaid", data.commission.unpaid, "text-amber-700 bg-amber-50"],
                  ["Paid", data.commission.paid, "text-emerald-700 bg-emerald-50"],
                  ["Non-commissionable", data.commission.non_commissionable, "text-slate-600 bg-slate-50"],
                ].map(([label, amount, cls]) => (
                  <div key={String(label)} className={`mb-3 rounded-md p-3 ${cls}`}>
                    <div className="text-xs">{label}</div>
                    <div className="text-xl font-semibold">{Number(amount).toLocaleString()} {currency}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="grid gap-4 lg:grid-cols-2">
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <div className="border-b border-slate-100 p-4">
                  <h2 className="text-sm font-semibold">{data.scope === "self" ? "Your performance" : "Staff performance"}</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500"><tr>
                      <th className="p-3">Staff</th><th className="p-3">Contacts</th><th className="p-3">Referrals</th>
                      <th className="p-3">Converted</th><th className="p-3">Rate</th>
                    </tr></thead>
                    <tbody>{data.staff_performance.map((row) => (
                      <tr key={row.user_id} className="border-t border-slate-100">
                        <td className="p-3 font-medium">{row.name}</td><td className="p-3">{row.contacts}</td>
                        <td className="p-3">{row.referrals}</td><td className="p-3">{row.conversions}</td>
                        <td className="p-3">{row.conversion_rate}%</td>
                      </tr>
                    ))}</tbody>
                  </table>
                  {!data.staff_performance.length && <p className="p-6 text-center text-sm text-slate-500">No staff activity in this view.</p>}
                </div>
              </div>
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <div className="border-b border-slate-100 p-4"><h2 className="text-sm font-semibold">Territory performance</h2></div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-500"><tr>
                      <th className="p-3">Territory</th><th className="p-3">Referrals</th><th className="p-3">Converted</th><th className="p-3">Rate</th>
                    </tr></thead>
                    <tbody>{data.territory_performance.map((row) => (
                      <tr key={row.territory} className="border-t border-slate-100">
                        <td className="p-3 font-medium">{row.territory}</td><td className="p-3">{row.referrals}</td>
                        <td className="p-3">{row.conversions}</td>
                        <td className="p-3">{row.referrals ? Math.round(row.conversions / row.referrals * 100) : 0}%</td>
                      </tr>
                    ))}</tbody>
                  </table>
                  {!data.territory_performance.length && <p className="p-6 text-center text-sm text-slate-500">No territory activity in this period.</p>}
                </div>
              </div>
            </section>
          </>
        )}
      </main>
    </>
  );
}
